import { z } from 'zod';
import { toTypedSchema } from '@vee-validate/zod';
import { translateApp } from "@/i18n";
export const serviceScheduleItemSchema = z.object({
    base_price: z.number().min(0),
    net_price: z.number().min(0),
    total_price: z.number().min(0),
    price_type: z.enum(['PERSON', 'HOUR']),
    commission_amount: z.number().min(0).default(0),
    commission_type: z.enum(['PERCENTAGE', 'FIXED']).default('PERCENTAGE'),
    standard_max_participants: z.number().int().min(1).default(1),
    overcapacity_max_participants: z.number().int().nullable().optional(),
    overflow_price: z.number().min(0).nullable().optional(),
    allow_reservations: z.boolean().default(true),
    days_of_week: z.array(z.enum(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'])).min(1),
    start_time: z.string(),
    end_time: z.string(),
});
export const serviceFormSchema = z.object({
    name: z
        .string()
        .min(1, translateApp("services.validation.name_required"))
        .max(255, translateApp("services.validation.name_max")),
    description: z
        .string()
        .optional()
        .nullable(),
    duration: z
        .number({ invalid_type_error: translateApp("services.validation.duration_number") })
        .min(0.25, translateApp("services.validation.duration_min")),
    provider_id: z
        .number({ invalid_type_error: translateApp("services.validation.provider_required") })
        .min(1, translateApp("services.validation.provider_required")),
    category_id: z
        .number()
        .int()
        .positive()
        .nullable()
        .optional(),
    service_type_id: z
        .number()
        .nullable()
        .optional(),
    country: z
        .string()
        .min(1, translateApp("services.validation.country_required")),
    state: z
        .string()
        .min(1, translateApp("services.validation.state_required"))
        .default('ACT'),
    location_one: z
        .string()
        .optional()
        .nullable(),
    location_two: z
        .string()
        .optional()
        .nullable(),
    min_time_booking: z
        .number({ invalid_type_error: translateApp("services.validation.number_required") })
        .min(0, translateApp("services.validation.min_booking_non_negative"))
        .int(translateApp("services.validation.integer_required"))
        .default(1),
    max_time_booking: z
        .number({ invalid_type_error: translateApp("services.validation.number_required") })
        .min(1, translateApp("services.validation.max_booking_min"))
        .int(translateApp("services.validation.integer_required"))
        .default(364),
    cancellation_policy_duration: z
        .number({ invalid_type_error: translateApp("services.validation.number_required") })
        .min(0, translateApp("services.validation.non_negative"))
        .int(translateApp("services.validation.integer_required"))
        .default(0),
    photo_storage_ids: z
        .array(z.number())
        .min(1, translateApp("services.validation.photos_required"))
        .default([]),
    additional_information: z
        .string()
        .max(255, translateApp("services.validation.additional_info_max"))
        .nullable()
        .optional(),
});
export const serviceFormSchemaRefined = serviceFormSchema.refine((data) => data.max_time_booking >= data.min_time_booking, {
    message: translateApp("services.validation.max_booking_gte_min"),
    path: ['max_time_booking'],
});
export const serviceEntitySchema = serviceFormSchema.extend({
    id: z.number(),
    version: z.number().int().nonnegative().optional(),
    created_by: z.string().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
    deleted_at: z.string().nullable().optional(),
    provider: z.object({
        id: z.number(),
        name: z.string(),
    }).optional(),
    category: z.object({
        id: z.number(),
        name: z.string(),
    }).nullable().optional(),
    service_type: z.object({
        id: z.number(),
        name: z.string(),
        slug: z.string(),
    }).nullable().optional(),
    photo_urls: z.array(z.string()).optional(),
});
export const serviceValidationSchema = toTypedSchema(serviceFormSchemaRefined);
export const defaultServiceFormValues = {
    name: '',
    description: null,
    duration: 1,
    provider_id: 0,
    category_id: null,
    service_type_id: null,
    country: '',
    state: 'ACT',
    location_one: null,
    location_two: null,
    min_time_booking: 1,
    max_time_booking: 364,
    cancellation_policy_duration: 0,
    photo_storage_ids: [],
    additional_information: null,
};
export function apiResponseToFormData(service) {
    return {
        name: service.name,
        description: service.description ?? null,
        duration: service.duration,
        provider_id: service.provider?.id ?? service.provider_id,
        category_id: service.category?.id ?? service.category_id ?? null,
        service_type_id: service.service_type?.id ?? service.service_type_id ?? null,
        country: service.country,
        state: service.state ?? 'ACT',
        location_one: service.location_one ?? null,
        location_two: service.location_two ?? null,
        min_time_booking: service.min_time_booking ?? 1,
        max_time_booking: service.max_time_booking ?? 364,
        cancellation_policy_duration: service.cancellation_policy_duration ?? 0,
        photo_storage_ids: service.photo_storage_ids ?? [],
        additional_information: service.additional_information ?? null,
    };
}
export function validateServiceForm(data) {
    return serviceFormSchemaRefined.safeParse(data);
}
